// src/components/MobileCalendarView.js
// Mobile-only calendar: dot grid + inline day task list

import React, { useMemo, useState } from "react";
import {
  Box,
  Stack,
  Typography,
  ButtonBase,
  Chip,
  Paper,
  IconButton,
} from "@mui/material";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";

/* ------------------------------------------------------------------ */
/* Color tokens (mirrors module-scope constants in PredictDisruption)  */
/* ------------------------------------------------------------------ */

const PRIORITY = { dot: "#FB7185", stripe: "#FECDD3", text: "#9F1239", bg: "#FFF1F2" };
const DEAL     = { dot: "#2563EB", stripe: "#C7D2FE", text: "#1E3A8A", bg: "#EEF2FF" };
const PERSONAL = { dot: "#7C3AED", stripe: "#E9D5FF", text: "#5B21B6", bg: "#FAF5FF" };
const NEUTRAL  = { dot: "#10B981", stripe: "#BBF7D0", text: "#065F46", bg: "#ECFDF5" };

/* ------------------------------------------------------------------ */
/* Helpers                                                             */
/* ------------------------------------------------------------------ */

const startOfDay = (d) => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
};

const dateKey = (d) => startOfDay(d).toISOString().slice(0, 10);

const todayStr = dateKey(new Date());

const isPriorityCall = (a) =>
  a?.source_type === "follow_up" &&
  String(a?.summary || "").toLowerCase().startsWith("call ");

const safeDateFromAlert = (a) => {
  if (typeof a?.days_until === "number") {
    const x = new Date();
    x.setDate(x.getDate() + a.days_until);
    return x;
  }
  if (a?.created_at) {
    const t = new Date(a.created_at);
    return Number.isNaN(t.getTime()) ? new Date() : t;
  }
  return new Date();
};

function getDotsForDay(events, dayStr) {
  const seen = new Set();
  const dots = [];
  for (const e of events) {
    if (!e.start) continue;
    if (dateKey(new Date(e.start)) !== dayStr) continue;
    const kind = e.extendedProps?.kind;
    const color =
      kind === "calls_group" ? PRIORITY.dot :
      kind === "deal"        ? DEAL.dot :
      kind === "personal"    ? PERSONAL.dot :
                               NEUTRAL.dot;
    if (!seen.has(color)) {
      seen.add(color);
      dots.push(color);
      if (dots.length === 3) break;
    }
  }
  return dots;
}

function buildDayItems(dayStr, alerts, manualEvents, model) {
  const items = [];

  const calls = model.callsByDay?.get(dayStr) || [];
  calls.forEach((a) => items.push({ kind: "call", a }));

  alerts.forEach((a) => {
    if (isPriorityCall(a)) return;
    if (dateKey(startOfDay(safeDateFromAlert(a))) !== dayStr) return;
    items.push({ kind: "alert", a });
  });

  manualEvents.forEach((m) => {
    if (m.dateKey === dayStr) items.push({ kind: "manual", m });
  });

  return items;
}

function getItemColor(item) {
  if (item.kind === "call") return PRIORITY;
  const src = item.a?.source_type;
  if (src === "deal_event") return DEAL;
  if (src === "birthday" || src === "anniversary") return PERSONAL;
  return NEUTRAL;
}

function getItemTitle(item) {
  return item.a?.summary || item.m?.title || item.a?.type || "Task";
}

function getItemSubtitle(item) {
  if (item.kind === "call") return "Priority call";
  if (item.a?.message) return item.a.message;
  if (item.kind === "manual") return item.m?.type || "Manual";
  return "";
}

function getSeverityBadge(item) {
  const sev = item.a?.severity;
  if (!sev || item.kind === "manual") return null;
  const map = {
    high:   { bg: "#FEE2E2", text: "#991B1B", label: "High" },
    medium: { bg: "#FEF3C7", text: "#92400E", label: "Med" },
    low:    { bg: "#F0FDF4", text: "#166534", label: "Low" },
  };
  return map[sev] || null;
}

/* ------------------------------------------------------------------ */
/* MobileCalendarGrid                                                  */
/* ------------------------------------------------------------------ */

function MobileCalendarGrid({ currentMonth, selectedDate, events, onSelectDate }) {
  const dayHeaders = useMemo(() => {
    // Jan 7, 2024 was a Sunday — index 0 = Sun … 6 = Sat
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(2024, 0, 7 + i);
      return new Intl.DateTimeFormat(undefined, { weekday: "short" })
        .format(d)
        .toUpperCase()
        .slice(0, 3);
    });
  }, []);

  const cells = useMemo(() => {
    const year  = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const first = new Date(year, month, 1);
    const gridStart = new Date(first);
    gridStart.setDate(gridStart.getDate() - first.getDay()); // back to Sunday

    return Array.from({ length: 42 }, (_, i) => {
      const d = new Date(gridStart);
      d.setDate(d.getDate() + i);
      const key = dateKey(d);
      return {
        date: d,
        key,
        isCurrentMonth: d.getMonth() === month,
        isToday:        key === todayStr,
        isSelected:     key === selectedDate,
        dots:           getDotsForDay(events, key),
      };
    });
  }, [currentMonth, selectedDate, events]);

  return (
    <Box>
      {/* Day-of-week headers */}
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", mb: 0.5 }}>
        {dayHeaders.map((h) => (
          <Typography
            key={h}
            align="center"
            sx={{ fontSize: 11, fontWeight: 900, color: "#94A3B8", py: 0.5 }}
          >
            {h}
          </Typography>
        ))}
      </Box>

      {/* Day cells */}
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: "2px" }}>
        {cells.map((cell) => (
          <ButtonBase
            key={cell.key}
            onClick={() => onSelectDate(cell.key)}
            aria-label={cell.date.toLocaleDateString(undefined, {
              month: "short",
              day: "numeric",
            })}
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              aspectRatio: "1",
              borderRadius: "50%",
              opacity: cell.isCurrentMonth ? 1 : 0.3,
              bgcolor: cell.isSelected
                ? "#2563EB"
                : cell.isToday
                ? "rgba(37,99,235,0.1)"
                : "transparent",
              transition: "background 150ms ease",
            }}
          >
            <Typography
              sx={{
                fontSize: 13,
                fontWeight: cell.isToday || cell.isSelected ? 900 : 500,
                color: cell.isSelected
                  ? "#fff"
                  : cell.isToday
                  ? "#2563EB"
                  : "#0F172A",
                lineHeight: 1,
              }}
            >
              {cell.date.getDate()}
            </Typography>

            {/* Colored dots */}
            <Box sx={{ display: "flex", gap: "2px", mt: "3px", minHeight: 7 }}>
              {cell.dots.map((color, i) => (
                <Box
                  key={i}
                  sx={{
                    width: 5,
                    height: 5,
                    borderRadius: "50%",
                    bgcolor: cell.isSelected ? "rgba(255,255,255,0.8)" : color,
                  }}
                />
              ))}
            </Box>
          </ButtonBase>
        ))}
      </Box>
    </Box>
  );
}

/* ------------------------------------------------------------------ */
/* MobileDayTaskList                                                   */
/* ------------------------------------------------------------------ */

function MobileDayTaskList({ items, selectedDate, onDayOpen }) {
  if (!items.length) {
    return (
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ py: 3, textAlign: "center" }}
      >
        Nothing scheduled for this day.
      </Typography>
    );
  }

  return (
    <Stack
      spacing={1.5}
      sx={{ maxHeight: "calc(100vh - 430px)", overflowY: "auto", pb: 2 }}
    >
      {items.map((item, i) => {
        const c       = getItemColor(item);
        const title   = getItemTitle(item);
        const sub     = getItemSubtitle(item);
        const badge   = getSeverityBadge(item);

        return (
          <Paper
            key={`${i}_${title}`}
            elevation={0}
            onClick={() => onDayOpen(selectedDate)}
            sx={{
              p: 1.5,
              borderRadius: 2.5,
              border: "1px solid",
              borderColor: c.stripe,
              borderLeft: `4px solid ${c.dot}`,
              bgcolor: c.bg,
              cursor: "pointer",
              display: "flex",
              flexDirection: "column",
              gap: 0.4,
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography
                sx={{ fontWeight: 900, fontSize: 14, color: c.text, flex: 1 }}
                noWrap
              >
                {title}
              </Typography>
              {badge && (
                <Chip
                  size="small"
                  label={badge.label}
                  sx={{
                    height: 20,
                    fontSize: 11,
                    fontWeight: 900,
                    bgcolor: badge.bg,
                    color: badge.text,
                    flexShrink: 0,
                  }}
                />
              )}
            </Stack>

            {sub ? (
              <Typography sx={{ fontSize: 12, color: c.text, opacity: 0.7 }}>
                {sub}
              </Typography>
            ) : null}
          </Paper>
        );
      })}
    </Stack>
  );
}

/* ------------------------------------------------------------------ */
/* Main export                                                         */
/* ------------------------------------------------------------------ */

export default function MobileCalendarView({
  model,
  alerts,
  manualEvents,
  loading,
  onDayOpen,
}) {
  const [currentMonth, setCurrentMonth] = useState(() => startOfDay(new Date()));
  const [selectedDate, setSelectedDate] = useState(todayStr);

  const prevMonth = () =>
    setCurrentMonth((m) => new Date(m.getFullYear(), m.getMonth() - 1, 1));
  const nextMonth = () =>
    setCurrentMonth((m) => new Date(m.getFullYear(), m.getMonth() + 1, 1));

  const monthLabel = new Intl.DateTimeFormat(undefined, {
    month: "long",
    year: "numeric",
  }).format(currentMonth);

  const dayItems = useMemo(
    () => buildDayItems(selectedDate, alerts, manualEvents, model),
    [selectedDate, alerts, manualEvents, model]
  );

  const selectedDateFormatted = useMemo(() => {
    const d = new Date(selectedDate + "T00:00:00");
    return d.toLocaleDateString(undefined, { day: "numeric", month: "long" });
  }, [selectedDate]);

  return (
    <Box>
      {/* Calendar card */}
      <Paper elevation={0} sx={{ p: 2, borderRadius: 2, mb: 2 }}>
        {/* Month navigation */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          mb={2}
        >
          <IconButton
            size="small"
            onClick={prevMonth}
            sx={{
              bgcolor: "#f8fafc",
              border: "1px solid #e5e7eb",
              borderRadius: 2,
              "&:hover": { bgcolor: "#eef2ff" },
            }}
          >
            <ChevronLeftRoundedIcon fontSize="small" />
          </IconButton>

          <Typography
            sx={{
              fontWeight: 900,
              fontSize: 16,
              textTransform: "capitalize",
              color: "#0F172A",
            }}
          >
            {monthLabel}
          </Typography>

          <IconButton
            size="small"
            onClick={nextMonth}
            sx={{
              bgcolor: "#f8fafc",
              border: "1px solid #e5e7eb",
              borderRadius: 2,
              "&:hover": { bgcolor: "#eef2ff" },
            }}
          >
            <ChevronRightRoundedIcon fontSize="small" />
          </IconButton>
        </Stack>

        <MobileCalendarGrid
          currentMonth={currentMonth}
          selectedDate={selectedDate}
          events={model.events}
          onSelectDate={setSelectedDate}
        />
      </Paper>

      {/* Day summary label */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        mb={1.5}
        px={0.5}
      >
        <Typography sx={{ fontWeight: 900, fontSize: 15, color: "#0F172A" }}>
          {selectedDateFormatted}
          {dayItems.length > 0 && (
            <Typography
              component="span"
              sx={{ color: "#64748B", fontWeight: 700, fontSize: 13, ml: 0.75 }}
            >
              — {dayItems.length} {dayItems.length === 1 ? "task" : "tasks"}
            </Typography>
          )}
        </Typography>

        {loading && (
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 800 }}>
            Refreshing…
          </Typography>
        )}
      </Stack>

      {/* Task cards */}
      <MobileDayTaskList
        items={dayItems}
        selectedDate={selectedDate}
        onDayOpen={onDayOpen}
      />
    </Box>
  );
}
